import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { logError, logSystemEvent } from '../utils/logs';

const ThemeContext = createContext(null);

const THEME_DARK = 'dark';
const THEME_LIGHT = 'light';
const THEME_STORAGE_KEY = 'theme_preference';

const LIGHT_ONLY_PATHS = new Set([
	'/',
	'/login',
	'/forgot-password',
	'/reset-password',
	'/activate-account',
	'/invite-complete',
	'/verify-totp',
	'/verify-backup-code',
	'/setup-totp',
]);

const isLightOnlyPath = () => {
	if (typeof window === 'undefined') return false;
	const raw = window.location.pathname;
	const pathname = raw === '/' ? '/' : raw.replace(/\/$/, '');
	return LIGHT_ONLY_PATHS.has(pathname);
};

const getSystemTheme = () => {
	if (typeof globalThis === 'undefined' || !globalThis.matchMedia) {
		return THEME_LIGHT;
	}

	const mediaQuery = globalThis.matchMedia('(prefers-color-scheme: dark)');
	if (!mediaQuery || typeof mediaQuery.matches !== 'boolean') {
		return THEME_LIGHT;
	}

	return mediaQuery.matches ? THEME_DARK : THEME_LIGHT;
};

const isValidTheme = value => value === THEME_LIGHT || value === THEME_DARK;

const getThemePreferenceFromStorage = () => {
	if (typeof globalThis === 'undefined' || !globalThis.localStorage) {
		return null;
	}

	try {
		const storedTheme = globalThis.localStorage.getItem(THEME_STORAGE_KEY);
		return isValidTheme(storedTheme) ? storedTheme : null;
	} catch (error) {
		logError('Failed to read theme preference from localStorage', error);
		return null;
	}
};

const persistThemePreference = themePreference => {
	if (typeof globalThis === 'undefined' || !globalThis.localStorage) {
		return;
	}

	try {
		globalThis.localStorage.setItem(THEME_STORAGE_KEY, themePreference);
	} catch (error) {
		logError('Failed to persist theme preference in localStorage', error, {
			themePreference,
		});
	}
};

export const ThemeProvider = ({ children }) => {
	const [theme, setTheme] = useState(() => getThemePreferenceFromStorage() ?? getSystemTheme());
	const [isSavingTheme, setIsSavingTheme] = useState(false);
	const [isLoadingThemePreference, setIsLoadingThemePreference] = useState(false);

	const applyThemeToDom = useCallback(activeTheme => {
		if (typeof document === 'undefined') {
			return;
		}

		const effectiveTheme = isLightOnlyPath() ? THEME_LIGHT : activeTheme;
		document.documentElement.setAttribute('data-theme', effectiveTheme);
		document.body.setAttribute('data-theme', effectiveTheme);
	}, []);

	const applyResolvedTheme = useCallback(
		themePreference => {
			const nextTheme = isValidTheme(themePreference) ? themePreference : getSystemTheme();
			setTheme(nextTheme);
			applyThemeToDom(nextTheme);
		},
		[applyThemeToDom]
	);

	useEffect(() => {
		applyThemeToDom(theme);
	}, [applyThemeToDom, theme]);

	useEffect(() => {
		if (typeof globalThis === 'undefined' || !globalThis.matchMedia) {
			return undefined;
		}

		const mediaQuery = globalThis.matchMedia('(prefers-color-scheme: dark)');
		if (!mediaQuery) {
			return undefined;
		}

		const handleThemeChange = event => {
			if (isValidTheme(getThemePreferenceFromStorage())) {
				return;
			}

			const nextTheme = event.matches ? THEME_DARK : THEME_LIGHT;
			setTheme(nextTheme);
			applyThemeToDom(nextTheme);
		};

		if (typeof mediaQuery.addEventListener === 'function') {
			mediaQuery.addEventListener('change', handleThemeChange);
			return () => mediaQuery.removeEventListener('change', handleThemeChange);
		}

		mediaQuery.addListener(handleThemeChange);
		return () => mediaQuery.removeListener(handleThemeChange);
	}, [applyThemeToDom]);

	const setThemePreference = useCallback(
		nextTheme => {
			if (!isValidTheme(nextTheme)) {
				throw new Error('Invalid theme preference');
			}

			setIsLoadingThemePreference(false);
			applyResolvedTheme(nextTheme);
			persistThemePreference(nextTheme);

			setIsSavingTheme(true);
			try {
				logSystemEvent('theme_preference_updated', 'info', {
					themePreference: nextTheme,
					persistence: 'local_storage',
				});
			} catch (error) {
				logError('Failed to persist theme preference in localStorage', error, {
					themePreference: nextTheme,
				});
				throw error;
			} finally {
				setIsSavingTheme(false);
			}
		},
		[applyResolvedTheme]
	);

	const toggleTheme = useCallback(() => {
		const nextTheme = theme === THEME_DARK ? THEME_LIGHT : THEME_DARK;
		return setThemePreference(nextTheme);
	}, [setThemePreference, theme]);

	const contextValue = useMemo(
		() => ({
			theme,
			isDarkMode: theme === THEME_DARK,
			isSavingTheme,
			isLoadingThemePreference,
			toggleTheme,
			setThemePreference,
		}),
		[theme, isSavingTheme, isLoadingThemePreference, toggleTheme, setThemePreference]
	);

	return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
};

ThemeProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export const useTheme = () => {
	const context = useContext(ThemeContext);
	if (!context) {
		throw new Error('useTheme must be used within a ThemeProvider');
	}
	return context;
};
